import { Temporal } from 'temporal-polyfill';
import { PayCycleLike } from '../index.js';
import { PlainDateInterval, FrequentTimeZones, InstantInterval, DaysOfWeek } from '../../temporal-utils/index.js';

/**
 * A pay cycle represents a date range used to calculate how much payment a
 * coach receives. It additionally contains the date for issuing payment,
 * usually five days after the end date of the pay cycle.
 */
export class PayCycleDTO extends PlainDateInterval implements PayCycleLike {
  /** The day the payment is issued. */
  readonly paymentPlainDate: Temporal.PlainDate;
  readonly startPlainDate = this.start;
  readonly endPlainDate = this.end;
  readonly depositPostedDate: string;

  constructor({ startPlainDate, endPlainDate, paymentPlainDate }: PayCycleLike) {
    super(startPlainDate, endPlainDate);
    this.paymentPlainDate = paymentPlainDate;
    this.depositPostedDate = `${formatPlainDateToAirtable(DaysOfWeek.addBusinessDays(paymentPlainDate, 1))} - ${formatPlainDateToAirtable(DaysOfWeek.addBusinessDays(paymentPlainDate, 3))}`;
  }

  equals = PayCycleDTO.strictEquals.bind(null, this);

  /**
   * Represents the primary key in Airtable.
   */
  get payPeriodId(): string {
    const { start, end, paymentPlainDate } = this;
    return `${start}/${end}|${paymentPlainDate}`;
  }

  get payPeriodInterval(): string {
    return `${formatPlainDateToAirtable(this.start)} - ${formatPlainDateToAirtable(this.end)}`;
  }

  get payDate(): string {
    return formatPlainDateToAirtable(this.paymentPlainDate);
  }

  /**
   * Converts this pay cycle to an end-inclusive instant interval, defaulting to
   * US Pacific time, the time zone we use for accounting.
   */
  toInstantInterval(timeZone: Temporal.TimeZoneLike = FrequentTimeZones.UsPacific): InstantInterval {
    const start = this.start.toZonedDateTime({ timeZone }).toInstant();
    const end = this.end.add({ days: 1 }).toZonedDateTime({ timeZone }).toInstant();
    return InstantInterval.from(start, end);
  }

  static fromPayPeriodId(payPeriodId: string): PayCycleDTO {
    const [interval, payment] = payPeriodId.split('|');

    const [start, end] = interval.split('/');

    return PayCycleDTO.from(
      Temporal.PlainDate.from(start),
      Temporal.PlainDate.from(end),
      Temporal.PlainDate.from(payment),
    );
  }

  static fromIntervalString(interval: string, paymentPlainDate?: Temporal.PlainDate): PayCycleDTO {
    const [start, end] = interval.split('/');
    return PayCycleDTO.from(Temporal.PlainDate.from(start), Temporal.PlainDate.from(end), paymentPlainDate);
  }

  static getDefaultPaymentDate(startPlainDate: Temporal.PlainDate): Temporal.PlainDate {
    if (startPlainDate.day === 11) {
      return startPlainDate.add({ months: 1 }).with({ day: 1 });
    }
    if (startPlainDate.day === 26) {
      return startPlainDate.add({ months: 1 }).with({ day: 15 });
    }
    throw new Error('Invalid start date');
  }

  /**
   * Create a pay cycle using the provided start date, end date, and payment
   * date.
   * @param paymentPlainDate If none given, defaults to `this.getDefaultPaymentDate(endPlainDate)`
   */
  static from(
    startPlainDate: Temporal.PlainDate,
    endPlainDate: Temporal.PlainDate,
    paymentPlainDate: Temporal.PlainDate = this.getDefaultPaymentDate(startPlainDate),
  ): PayCycleDTO {
    return new this({ startPlainDate, endPlainDate, paymentPlainDate });
  }

  /**
   * Returns the pay cycle that the given date falls under.
   *
   * This recurrence is designed to always partition a month into two pay cycles
   * without accumulating rounding errors that can be introduced by too much
   * duration arithmetic, making it easier to predict which pay cycle we are in.
   */
  static ongoing(now: Temporal.PlainDate): PayCycleDTO {
    if (now.day < 11) {
      // We are in the "cross from previous month" pay cycle; begin the range by
      // moving one month backward, then setting the day.
      return this.from(now.subtract({ months: 1 }).with({ day: 26 }), now.with({ day: 10 }));
    }
    if (now.day >= 11 && now.day < 26) {
      // We are in the "same month" pay cycle.
      return this.from(now.with({ day: 11 }), now.with({ day: 25 }));
    }
    // We are in the "cross into next month" pay cycle; end the range by moving
    // one month forward, then setting the day. The "next month" is allowed to
    // wrap into next year.
    return this.from(now.with({ day: 26 }), now.add({ months: 1 }).with({ day: 10 }));
  }

  static previous(now: Temporal.PlainDate): PayCycleDTO {
    const currentPaycycle = this.ongoing(now);

    return this.ongoing(currentPaycycle.startPlainDate.subtract({ days: 14 }));
  }

  /**
   * Returns the pay cycle that comes after the one that the given date falls
   * under. This is calculated by adding two weeks to the given date.
   */
  static upcoming(now: Temporal.PlainDate): PayCycleDTO {
    return this.ongoing(now.add({ weeks: 2 }));
  }

  /**
   * Compares two pay cycles for strict equality. (Same start, end, and payment date)
   */
  static strictEquals(a: PayCycleLike, b: PayCycleLike): boolean {
    return (
      Temporal.PlainDate.compare(a.startPlainDate, b.startPlainDate) === 0 &&
      Temporal.PlainDate.compare(a.endPlainDate, b.endPlainDate) === 0 &&
      Temporal.PlainDate.compare(a.paymentPlainDate, b.paymentPlainDate) === 0
    );
  }

  /**
   * Compares two pay cycles for soft equality. (Same start and end date)
   */
  static softEquals(a: PayCycleLike, b: PayCycleLike): boolean {
    return (
      Temporal.PlainDate.compare(a.startPlainDate, b.startPlainDate) === 0 &&
      Temporal.PlainDate.compare(a.endPlainDate, b.endPlainDate) === 0
    );
  }

  /**
   * Checks if the given pay cycle is the default pay cycle for the given date. (payment date is either day 1 or 15)
   */
  static isDefaultPayDate(payCycleId: string): boolean {
    const payCycle = PayCycleDTO.fromPayPeriodId(payCycleId);

    const defaultPayCycle = PayCycleDTO.ongoing(payCycle.startPlainDate);

    return PayCycleDTO.strictEquals(payCycle, defaultPayCycle);
  }

  static paycycleWindow(now: Temporal.PlainDate): PayCycleDTO {
    const current = PayCycleDTO.ongoing(now);
    const prev = PayCycleDTO.previous(now);
    const isInPrevWindow =
      Temporal.PlainDate.compare(now, prev.startPlainDate) >= 0 &&
      Temporal.PlainDate.compare(now, prev.paymentPlainDate) <= 0;

    if (isInPrevWindow) {
      return prev;
    }

    return current;
  }

  static isWithinWindow(now: Temporal.PlainDate, payCycle: PayCycleDTO): boolean {
    return (
      Temporal.PlainDate.compare(now, payCycle.startPlainDate) >= 0 &&
      Temporal.PlainDate.compare(now, payCycle.paymentPlainDate) <= 0
    );
  }
}

export function formatPlainDateToAirtable(date: Temporal.PlainDate): string {
  const { isoMonth, isoDay, isoYear } = date.getISOFields();
  const month = isoMonth.toString();
  const day = isoDay.toString();
  const year = isoYear.toString();
  return `${month}/${day}/${year}`;
}
